import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  Badge,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { keyframes } from "@mui/system";

import {
  LightMode,
  DarkMode,
  Settings,
  Person,
  Logout,
  TrackChanges,
  Mail,
  NotificationsNone,
  Notifications,
  Security,
  Dns, // CS-2018
  Schedule,
  SupportAgent,
  InfoOutlined,
  Cancel,
  MailOutline,
  Search as SearchIcon,
} from "@mui/icons-material";
import CommandPalette from "./CommandPalette";
import { ColorModeContext } from "../../theme";
import styled from "@emotion/styled";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { logout, selectIsValueFoundInToken, selectTokenValues, useAuthSlice } from "../../redux/slice/authSlice";
import { useProSidebar } from "react-pro-sidebar";
import { Menu as MenuIcon } from "@mui/icons-material";
import CustomTooltip from "./CustomTooltip";
import OptionsPopup from "./OptionsPopup";
import { RootState } from "../../redux/store";
import { useSelector } from "react-redux";
import { CAN_ACCESS_APPLICATION, CAN_ACCESS_USER_PERMISSION, USER_ROLES } from "../../constants/applicationConstants";
import { useNavigation } from "../../hooks/useNavigation";
import { removeItemFromSessionStorage } from "../../utils/StorageUtils";
import { fetchInboxMessageList, fetchNotificationList, resetNotificationData } from "../../redux/slice/inbox/createNotificationSlice";
import { useBranding } from "../../BrandingContext";
import WarningPopupNew from "./WarningPopupNew";
import CompletePracticeRegistrationModal from "../operation/account/CompletePracticeRegistrationModal";
import MedicalNoteAuditInFlightButton from "../operation/aiMedicalAudit/MedicalNoteAuditInFlightButton";
import { useModalStyles } from "./useModalStyles";

const SpinningSettingsIcon = styled(Settings)`
  @keyframes toolbarConfigSpin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  animation: toolbarConfigSpin 10s linear infinite;
  transform-origin: center center;
`;

const CustomToolbar = () => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const { userInfo } = useAuthSlice();
  const { tokens: mTokens, isDark } = useModalStyles();
  const { toggleSidebar } = useProSidebar();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorE2, setAnchorE2] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const openV3ServicePhoneNumber = Boolean(anchorE2);
  const [showInfoPopup, setShowInfoPopup] = useState<{ message: string, show: boolean }>({ message: "", show: false });
  const canAccessConfig = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.CONFIG_MANAGEMENT)(state));
  const canAccessAudit = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.AUDITLOG_MANAGEMENT)(state));
  const canAccessInbox = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.INBOX_MANAGEMENT)(state));
  const canAccessJobSchedular = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.JOB_SCHEDULER_MANAGEMENT,)(state));//CS-1571
  const canAccessServerManagement = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.SERVER_MANAGEMENT,)(state)); // CS-2018 - EC2 server dashboard
  const canAccessNotifications = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.NOTIFICATION_MANAGEMENT)(state));
  const accessibleNotifications = useSelector((state: RootState) => selectTokenValues("canAccessNotifications")(state));
  const canAccessUserPermissionView = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessUserPermission", CAN_ACCESS_USER_PERMISSION.VIEW)(state));
  const [userNotificationCount, setUserNotificationCount] = useState<number>(0);
  const [systemNotificationCount, setSystemNotificationCount] = useState<number>(0);
  const notifications = useSelector((state: RootState) => state.createNotificationSlice.notifications);
  const branding = useBranding();
  const { supportContactInformation } = branding || {};
  const [showPRUWarning, setShowPRUWarning] = useState(false);

  // Per-action tint — turns the once-uniform grey icon row into a legible, color-coded cluster.
  const iconBtnSx = (color: string = mTokens.accent) => ({
    borderRadius: "10px",
    color: mTokens.textSecondary,
    width: 36,
    height: 36,
    transition: "color 0.2s ease, background-color 0.2s ease, transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s ease",
    "&:hover": {
      color,
      backgroundColor: isDark ? `${color}20` : `${color}14`,
      transform: "translateY(-2px)",
      boxShadow: `0 6px 14px ${color}30`,
    },
    "&:active": {
      transform: "translateY(0) scale(0.94)",
    },
    "& .MuiSvgIcon-root": { fontSize: "1.2rem", transition: "transform 0.2s ease" },
    "&:hover .MuiSvgIcon-root": { transform: "scale(1.08)" },
  });

  const badgeSx = (color: string = mTokens.accent) => ({
  "& .MuiBadge-badge": {
    backgroundColor: color,
    color: "#fff",
    fontSize: "0.62rem",
    fontWeight: 700,
    lineHeight: 1,
    height: "20px !important",
    minWidth: "20px !important",
    width: "auto !important",
    padding: "0 2px !important",
    borderRadius: "10px !important",
    top: "-3px",
    right: "-3px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1.5px solid ${isDark ? "#141720" : "#fff"}`,
  },
});

  const pulseRing = (rgb: string) => keyframes`
    0%   { box-shadow: 0 0 0 0 rgba(${rgb},0.45); }
    70%  { box-shadow: 0 0 0 8px rgba(${rgb},0); }
    100% { box-shadow: 0 0 0 0 rgba(${rgb},0); }
  `;

  const alertIconBtnSx = (color: string, rgb: string, active: boolean) => ({
    ...iconBtnSx(color),
    animation: active ? `${pulseRing(rgb)} 2.4s ease-in-out infinite` : "none",
  });

  // One color per action — makes the cluster scannable instead of a wall of identical grey glyphs.
  const actionColors = {
    contact: "#3b82f6",
    notifications: "#f59e0b",
    inbox: "#8b5cf6",
    agent: "#6366f1",
    servers: "#06b6d4",
    mode: isDark ? "#f59e0b" : "#7c3aed",
    security: "#f43f5e",
    config: "#64748b",
  };

  const menuPaperSx = {
    overflow: "visible",
    mt: 1.5,
    borderRadius: "14px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    boxShadow: isDark
      ? "0 16px 40px rgba(0,0,0,0.45)"
      : "0 16px 40px rgba(15,23,42,0.12)",
    "& .MuiAvatar-root": {
      width: 32,
      height: 32,
      ml: -0.5,
      mr: 1,
    },
    "& .MuiMenuItem-root": {
      borderRadius: "8px",
      mx: 0.75,
      my: 0.35,
      fontSize: "0.88rem",
      fontWeight: 600,
      color: mTokens.textPrimary,
      "&:hover": {
        backgroundColor: isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)",
        color: mTokens.accent,
      },
    },
    "& .MuiDivider-root": {
      borderColor: mTokens.border,
      my: 0.75,
    },
    "&::before": {
      content: '""',
      display: "block",
      position: "absolute",
      top: 0,
      right: 14,
      width: 10,
      height: 10,
      bgcolor: mTokens.bg,
      borderLeft: `1px solid ${mTokens.border}`,
      borderTop: `1px solid ${mTokens.border}`,
      transform: "translateY(-50%) rotate(45deg)",
      zIndex: 0,
    },
  };

  const handlePRURestricted = (callback: () => void) => {
    if (userInfo.userRole === USER_ROLES.ROLE_PRACTICE_USER_PRU) {
      setShowPRUWarning(true);
      return;
    }
    callback();
  };

  const fetchUserNotifData = async () => {
    const resultAction = await dispatch(fetchInboxMessageList({
      notificationTypes: accessibleNotifications ? accessibleNotifications : [],
      unread: true,
      notificationCategory: "user",
      currentPage: 1
    }));
    if (fetchInboxMessageList.fulfilled.match(resultAction)) {
      const data = resultAction.payload.data;
        setUserNotificationCount(data.content?.length ?? 0);
      
    }
  };

  const fetchSystemNotifData = async () => {
    const resultAction = await dispatch(fetchNotificationList({
      notificationTypes: accessibleNotifications ? accessibleNotifications : [],
      unread: true,
      notificationCategory: "system",
      currentPage: 1,
      itemsPerPage:5
    }));
    if (fetchNotificationList.fulfilled.match(resultAction)) {
      const data = resultAction.payload.data;

        setSystemNotificationCount(data.totalElements);
    }
  };

  useEffect(() => {

    if (userInfo.notificationEnable) {
      fetchUserNotifData();
      fetchSystemNotifData();
    }
  }, []);


  useEffect(() => {
    if(!notifications) return;

    const notif = notifications[0];
    if (!notif) return;

    if (userInfo.notificationEnable) {
      if (notifications[0].ackedAll === true) {
        if(notifications[0].senderId != null){
          fetchUserNotifData();
        } else {
          fetchSystemNotifData();
        }
        
      } else {
        if (!(notifications[0].notificationAckId) && accessibleNotifications.includes(notifications[0].notificationType ?? "")) {
          if (notifications[0].senderId != null) {

            const isSentByLoggedUser = notifications[0].senderId === userInfo.userId;
            const hasNoChildNotifications =
              !notifications[0].childNotifications ||
              notifications[0].childNotifications.length === 0;
            const isGroupMessage = notifications[0].notificationType != "USER"

            const shouldSkip = isSentByLoggedUser && hasNoChildNotifications && isGroupMessage;

            if(!shouldSkip){
              setUserNotificationCount((prev) => prev + 1);
            }
          } else {
            setSystemNotificationCount((prev) => prev + 1);
          }

        } else {
          const isCurrentUserAck = notif.ackUsrId === userInfo.userId;
          const isAckOnceByOtherUser = notif.ackUsrId !== userInfo.userId && notif.ackType === "ACK_ONCE";

          if ((typeof notif.ackUsrId !== 'undefined') && (isCurrentUserAck || isAckOnceByOtherUser) 
            && accessibleNotifications.includes(notifications[0].notificationType ?? "")) {

            if (notifications[0].senderId != null) {

              const isSentByLoggedUser = notifications[0].senderId === userInfo.userId;
              const hasNoChildNotifications =
                !notifications[0].childNotifications ||
                notifications[0].childNotifications.length === 0;
              const isGroupMessage = notifications[0].notificationType != "USER"

              const shouldSkip = isSentByLoggedUser && hasNoChildNotifications && isGroupMessage;

              if(!shouldSkip){
                setUserNotificationCount((prev) => Math.max(prev - 1, 0));
              }
            } else {
              setSystemNotificationCount((prev) => Math.max(prev - 1, 0));
            }
          }
        }
      }
    }
  }, [notifications]);



  const onClickLink = (linkName: string) => {
    navigate("/setting");
  };
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  // cs-890
  const handleClickPhoneNumber = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorE2(event.currentTarget);
  };
  const handleClosePhoneNumber = () => {
    setAnchorE2(null);
  };
  // 
  const signUpTwoAuthentication = () => {
    navigate("/signup2fa");
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    setAnchorEl(null);
    dispatch(logout());
    dispatch(resetNotificationData())
    //CS-1071 - Start
    removeItemFromSessionStorage("activeActionButtonId");
    removeItemFromSessionStorage("selections");
    //CS-1071 - End
    localStorage.setItem("logout", Date.now().toString());
    navigate("/");
  };
  const handleSettingsClick = () => {
    navigate('/configuration');
  };
  const handleClickAudit = () => {
    navigate("/auditlog");
  };
  const handleProfile = () => {
    navigate("/accountProfile");
  };

  const onClosePopup = () => {
    setShowInfoPopup({ message: "", show: false })
  }
  const onShowPopup = () => {
    setShowInfoPopup({ message: "You are about to sign out. Do you want to continue?", show: true })
  }

  const [profileSrc, setProfileSrc] = useState<string | undefined>(undefined);

   useEffect(() => {
    const doc = (userInfo as any)?.profileDocument;
    if (!doc) {
      setProfileSrc(undefined);
      return;
    }
    if (doc.content && doc.fileType) {
      setProfileSrc(`data:${doc.fileType};base64,${doc.content}`);
      return;
    }
    setProfileSrc(undefined);
  }, [userInfo]);

  const handleClickPermissions = (data: any) => {
    navigate("/user_permissions");
  }

  const isRestrictedUser = userInfo.userRole === USER_ROLES.ROLE_PRACTICE_USER_PRU;
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <>
      <AppBar
        position="sticky"
        color="transparent"
        elevation={0}
        sx={{
          boxShadow: "none",
          backdropFilter: "blur(10px)",
          backgroundColor: isDark ? "rgba(20,23,32,0.88)" : "rgba(255,255,255,0.88)",
          borderBottom: `1px solid ${mTokens.border}`,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            position: "relative",
            px: { xs: 1, sm: 1.5 },
            py: { xs: 0.75, sm: 1 },
            minHeight: { xs: 52, sm: 56 },
          }}
        >
          {/* Mobile only — on desktop the sidebar itself has its own open/close control
              once it's collapsed, so this toolbar button would be redundant there. */}
          <IconButton
            onClick={() => toggleSidebar()}
            size="small"
            aria-label="Open sidebar"
            sx={{ ...iconBtnSx(), display: { xs: "inline-flex", sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Compact fallback: shown only when there isn't room for the full pill below
              (sm–md), so it can never overlap the right-side icon cluster. The full pill
              takes over again at lg+. Styled as a mini version of the full search pill
              (rounded border + subtle fill + "Ctrl K" badge) rather than a bare icon. */}
          <Box
            role="button"
            aria-label="Open search"
            onClick={() => setCommandPaletteOpen(true)}
            sx={{
              display: { xs: "none", sm: "flex", lg: "none" },
              alignItems: "center",
              gap: 0.6,
              height: 36,
              px: 1,
              borderRadius: "10px",
              border: `1px solid ${mTokens.border}`,
              backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
              cursor: "pointer",
              transition: "border-color 0.2s ease, background-color 0.2s ease",
              "&:hover": {
                borderColor: mTokens.accent,
                backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.05)",
              },
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: mTokens.textSecondary }} />
            <Box
              sx={{
                px: 0.8,
                py: 0.1,
                borderRadius: "6px",
                border: `1px solid ${mTokens.border}`,
                fontSize: "0.66rem",
                fontWeight: 700,
                color: mTokens.textSecondary,
                lineHeight: 1.6,
              }}
            >
              Ctrl K
            </Box>
          </Box>

          {/* Sits in the open header between the sidebar and the icon cluster, so the
              pill stays centered there as the sidebar expands and collapses. */}
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
              minWidth: 0,
              px: 1.5,
            }}
          >
            <Box
              role="button"
              aria-label="Open search"
              onClick={() => setCommandPaletteOpen(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.4,
                py: 0.7,
                width: "100%",
                maxWidth: 380,
                borderRadius: "10px",
                border: `1px solid ${mTokens.border}`,
                backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
                cursor: "pointer",
                transition: "border-color 0.2s ease, background-color 0.2s ease",
                "&:hover": {
                  borderColor: mTokens.accent,
                  backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.05)",
                },
              }}
            >
              <SearchIcon sx={{ fontSize: 18, color: mTokens.textSecondary }} />
              <Typography
                noWrap
                sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
              >
                Search patients, invoices, orders...
              </Typography>
              <Box
                sx={{
                  px: 0.8,
                  py: 0.1,
                  borderRadius: "6px",
                  border: `1px solid ${mTokens.border}`,
                  fontSize: "0.66rem",
                  fontWeight: 700,
                  color: mTokens.textSecondary,
                  lineHeight: 1.6,
                }}
              >
                Ctrl K
              </Box>
            </Box>
          </Box>

          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-end", sm: "center" }}
            justifyContent="flex-end"
            flexWrap="nowrap"
            gap={{ xs: 0.5, sm: 1 }}
            sx={{ width: "auto", flexShrink: 0, minWidth: 0, ml: { xs: "auto", lg: 0 } }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: 0.35,
                p: 0.4,
                maxWidth: { xs: "100%", sm: "none" },
                borderRadius: "14px",
                backgroundColor: isDark ? "rgba(255,255,255,0.025)" : "rgba(15,23,42,0.025)",
                border: `1px solid ${mTokens.border}`,
              }}
            >
              {/* CS-890 */}
              <CustomTooltip title="Contact & Support" arrow>
                <IconButton
                  onClick={handleClickPhoneNumber}
                  size="small"
                  /* aria-controls={open ? "v3ServicePhoneNumber" : undefined} */
                  aria-controls={openV3ServicePhoneNumber ? "v3ServicePhoneNumber" : undefined} // CS-1984
                  aria-haspopup="true"
                  /* aria-expanded={open ? "true" : undefined} */
                  aria-expanded={openV3ServicePhoneNumber ? "true" : undefined} // CS-1984
                  sx={iconBtnSx(actionColors.contact)}
                >
                  <SupportAgent />
                  
                </IconButton>
              </CustomTooltip>

              {canAccessNotifications && userInfo.notificationEnable && (
                <CustomTooltip title="Notifications" arrow>
                  <IconButton
                    onClick={() => handlePRURestricted(() => navigate("/notification_list"))}
                    sx={alertIconBtnSx(actionColors.notifications, "245,158,11", systemNotificationCount > 0)}
                  >
                    <Badge
                      badgeContent={systemNotificationCount}
                      overlap="circular"
                      anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      sx={badgeSx(actionColors.notifications)}
                    >
                      {systemNotificationCount > 0 ? (
                        <Notifications sx={{ color: actionColors.notifications }} />
                      ) : (
                        <NotificationsNone sx={{ color: actionColors.notifications }} />
                      )}
                    </Badge>
                  </IconButton>
                </CustomTooltip>
              )}

              {canAccessInbox && userInfo.notificationEnable && (
                <CustomTooltip title="Inbox" arrow>
                  <IconButton
                    onClick={() => handlePRURestricted(() => navigate("/inbox_list"))}
                    sx={alertIconBtnSx(actionColors.inbox, "139,92,246", userNotificationCount > 0)}
                  >
                    <Badge
                      badgeContent={userNotificationCount}
                      overlap="circular"
                      anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      sx={badgeSx(actionColors.inbox)}
                    >
                      {userNotificationCount > 0 ? (
                       <Mail sx={{ color: actionColors.inbox }} />
                      ) : (
                        <MailOutline />
                      )}
                    </Badge>
                  </IconButton>
                </CustomTooltip>
              )}

              {canAccessJobSchedular && (
                <CustomTooltip title="Agent" arrow>
                  <IconButton onClick={() => navigate("/agent_list")} sx={iconBtnSx(actionColors.agent)}>
                    <Schedule />
                  </IconButton>
                </CustomTooltip>
              )}

              {/* CS-2018 START - EC2 server dashboard */}
              {canAccessServerManagement && (
                <CustomTooltip title="Servers" arrow>
                  <IconButton onClick={() => navigate("/server_management")} sx={iconBtnSx(actionColors.servers)}>
                    <Dns />
                  </IconButton>
                </CustomTooltip>
              )}
              {/* CS-2018 END */}

              {/* CS-1773 - Run Medical Note Audit AI Tool in Background */}
              <MedicalNoteAuditInFlightButton />

              <CustomTooltip title="Display Mode" arrow>
                <IconButton onClick={colorMode.toggleColorMode} sx={iconBtnSx(actionColors.mode)}>
                  {theme.palette.mode === "dark" ? (
                    <LightMode />
                  ) : (
                    <DarkMode />
                  )}
                </IconButton>
              </CustomTooltip>

              {canAccessUserPermissionView && (
                <CustomTooltip title="User Role Management" arrow>
                  <IconButton onClick={(e) => handleClickPermissions(e)} sx={iconBtnSx(actionColors.security)}>
                    <Security />
                  </IconButton>
                </CustomTooltip>
              )}

              {canAccessConfig && (
                <CustomTooltip title="Configuration" arrow>
                  <IconButton onClick={handleSettingsClick} sx={iconBtnSx(actionColors.config)}>
                    <SpinningSettingsIcon />
                  </IconButton>
                </CustomTooltip>
              )}

              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderColor: mTokens.border, my: 0.75, mx: 0.35 }}
              />

              <CustomTooltip title="Account" arrow placement="top-end">
                <IconButton
                  onClick={(e) => handlePRURestricted(() => handleClick(e))}
                  size="small"
                  aria-controls={open ? "account-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  sx={{
                    ...iconBtnSx(mTokens.accent),
                    p: 0.35,
                    width: 40,
                    height: 40,
                  }}
                >
                  <Avatar
                    src={profileSrc}
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: !profileSrc
                        ? isDark
                          ? "rgba(20,184,166,0.18)"
                          : "rgba(20,184,166,0.12)"
                        : "transparent",
                      color: mTokens.accent,
                      border: `1px solid ${isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.22)"}`,
                    }}
                  >
                    {!profileSrc && <Person />}
                  </Avatar>
                </IconButton>
              </CustomTooltip>
            </Box>
          </Box>

          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{ elevation: 0, sx: menuPaperSx }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.5, minWidth: 220 }}>
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Avatar
                  src={profileSrc}
                  sx={{
                    width: "44px !important",
                    height: "44px !important",
                    margin: "0 !important",
                    backgroundColor: !profileSrc
                      ? isDark
                        ? "rgba(20,184,166,0.18)"
                        : "rgba(20,184,166,0.12)"
                      : "transparent",
                    color: mTokens.accent,
                    border: `1px solid ${isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.22)"}`,
                  }}
                >
                  {!profileSrc && <Person />}
                </Avatar>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 1,
                    right: 1,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "#22c55e",
                    border: `2px solid ${mTokens.bg}`,
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 0, lineHeight: 1.25 }}>
                <Typography
                  noWrap
                  title={userInfo.userName}
                  sx={{ color: mTokens.textPrimary, fontSize: "0.9rem", fontWeight: 700 }}
                >
                  {userInfo.userName}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    display: "inline-block",
                    mt: 0.35,
                    color: mTokens.accent,
                    fontSize: "0.66rem",
                    fontWeight: 700,
                    letterSpacing: "0.03em",
                    textTransform: "uppercase",
                    backgroundColor: isDark ? "rgba(20,184,166,0.14)" : "rgba(20,184,166,0.1)",
                    borderRadius: "999px",
                    px: 0.9,
                    py: 0.15,
                  }}
                >
                  {userInfo.roleDisplayValue}
                </Typography>
              </Box>
            </Box>
            <Divider />
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <Person fontSize="small" sx={{ color: mTokens.textSecondary }} />
              </ListItemIcon>
              Account Profile
            </MenuItem>
            <Divider />
            {canAccessAudit && (
              <>
                <MenuItem onClick={handleClickAudit}>
                  <ListItemIcon>
                    <TrackChanges fontSize="small" sx={{ color: mTokens.textSecondary }} />
                  </ListItemIcon>
                  Audit Log
                </MenuItem>
                <Divider />
              </>
            )}
            <MenuItem onClick={onShowPopup}>
              <ListItemIcon>
                <Logout fontSize="small" sx={{ color: mTokens.textSecondary }} />
              </ListItemIcon>
              Sign Out
            </MenuItem>
          </Menu>

          {/* CS-890 */}
          <Menu
            anchorEl={anchorE2}
            open={openV3ServicePhoneNumber}
            id="v3ServicePhoneNumber"
            onClose={handleClosePhoneNumber}
            PaperProps={{
              elevation: 0,
              sx: {
                ...menuPaperSx,
                maxWidth: 320,
                px: 0.5,
                py: 0.5,
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", p: 1.5 }}>
              <Box>
                <Typography
                  mb={1}
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.95rem",
                    textAlign: "center",
                    color: mTokens.textPrimary,
                  }}
                >
                  Contact & Support
                </Typography>

                {supportContactInformation?.supportFormUrl && (
                  <Typography
                    mt={1}
                    variant="body2"
                    sx={{ color: mTokens.textSecondary, fontSize: "0.82rem", textAlign: "center" }}
                  >
                    <span
                      onClick={() => {
                        window.open(supportContactInformation?.supportFormUrl, "_blank");
                      }}
                      style={{
                        color: mTokens.accent,
                        cursor: "pointer",
                        fontWeight: 700,
                        paddingRight: 5,
                      }}
                    >
                      Click here
                    </span>
                    to create support ticket describing your support need.
                  </Typography>
                )}

                <Typography
                  mt={1}
                  variant="body2"
                  sx={{ color: mTokens.textSecondary, fontSize: "0.82rem", textAlign: "center" }}
                >
                  {supportContactInformation?.supportText}
                  <br />
                  <a
                    href={`tel:${supportContactInformation?.supportPhoneHref}`}
                    style={{
                      textDecoration: "none",
                      color: mTokens.accent,
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                  >
                    {supportContactInformation?.supportPhone}
                  </a>
                </Typography>
              </Box>
            </Box>
          </Menu>
        </Box>

        {/* Mobile: the search pill above is hidden at xs, so it gets its own full-width
            row here instead, right under the main toolbar row. */}
        <Box
          role="button"
          aria-label="Open search"
          onClick={() => setCommandPaletteOpen(true)}
          sx={{
            display: { xs: "flex", sm: "none" },
            alignItems: "center",
            gap: 1,
            mx: 1,
            mb: 1,
            px: 1.4,
            py: 0.7,
            borderRadius: "10px",
            border: `1px solid ${mTokens.border}`,
            backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
            cursor: "pointer",
            transition: "border-color 0.2s ease, background-color 0.2s ease",
            "&:hover": {
              borderColor: mTokens.accent,
              backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.05)",
            },
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: mTokens.textSecondary }} />
          <Typography
            noWrap
            sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            Search patients, invoices, orders...
          </Typography>
          <Box
            sx={{
              px: 0.8,
              py: 0.1,
              borderRadius: "6px",
              border: `1px solid ${mTokens.border}`,
              fontSize: "0.66rem",
              fontWeight: 700,
              color: mTokens.textSecondary,
              lineHeight: 1.6,
            }}
          >
            Ctrl K
          </Box>
        </Box>
      </AppBar>
      <OptionsPopup
        open={showInfoPopup.show}
        onClose={onClosePopup}
        variant="custom"
        message={showInfoPopup.message}
        title="Sign Out Confirmation"
        buttons={[
          { name: "Continue", color: "primary", onClick: handleLogout },
          { name: "Cancel", color: "secondary", onClick: onClosePopup }
        ]}
      />

      <WarningPopupNew
        open={showPRUWarning}
        onClose={() => setShowPRUWarning(false)}
        variant="warning"
        message={
          <center>
            {isRestrictedUser && userInfo.associatedPracticeStatus === "PENDING"
              ?
              "You don't have access to this feature. Please complete the practice registration in order to use the full system functionality."
              : "Your practice submission is pending approval. Full access to the system will be granted once it has been reviewed and approved."}
          </center>
        }
        title="Pre Registered User Warning"
        buttons={[
          ...(isRestrictedUser && userInfo.associatedPracticeStatus === "PENDING"
            ? [
              {
                name: "Cancel",
                color: "error" as const,
                icon: <Cancel sx={{ fontSize: "1rem" }} />,
                onClick: () => setShowPRUWarning(false),
              },
            ]
            : [
              {
                name: "OK",
                color: "success" as const,
                onClick: () => setShowPRUWarning(false),
              },
            ]),
          ...(isRestrictedUser && userInfo.associatedPracticeStatus === "PENDING"
            ? [
              {
                name: "Complete Registration",
                color: "success" as const,
                onClick: () => {
                  setRegistrationModalOpen(true);
                  setShowPRUWarning(false);
                },
              },
            ]
            : []),
        ]}
      />
      {isRestrictedUser && userInfo.associatedPracticeStatus === "PENDING" && (
        <Box
          sx={{
            background: "linear-gradient(135deg, rgba(15,118,110,0.85) 0%, rgba(17,94,89,0.9) 50%, rgba(13,148,136,0.8) 100%)",
            backdropFilter: "blur(12px)",
            px: { xs: 2, sm: 3 },
            py: { xs: 1.25, sm: 1 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "center",
            gap: { xs: 1.25, sm: 1 },
            border: "1px solid rgba(94,234,212,0.15)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.06)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(94,234,212,0.3), transparent)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <InfoOutlined sx={{ color: "#5eead4", fontSize: 18, mt: "2px", flexShrink: 0 }} />
            <Typography
              sx={{
                color: "#e0f2f1",
                fontSize: { xs: "0.82rem", sm: "0.88rem" },
                fontWeight: 500,
                letterSpacing: "0.01em",
                lineHeight: 1.5,
              }}
            >
              Welcome to V3 Biomed! We're excited to have you on board. To access our
              products and all features, please complete your registration.
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="small"
            onClick={() => setRegistrationModalOpen(true)}
            sx={{
              alignSelf: { xs: "stretch", sm: "center" },
              textTransform: "none",
              color: "#fff",
              fontWeight: 600,
              fontSize: { xs: "0.8rem", sm: "0.78rem" },
              borderRadius: 2,
              px: { xs: 2, sm: 2.5 },
              py: 0.75,
              whiteSpace: "nowrap",
              flexShrink: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              border: "1px solid rgba(94,234,212,0.25)",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.35)",
                borderColor: "rgba(94,234,212,0.5)",
                boxShadow: "none",
              },
            }}
          >
            Complete Registration
          </Button>
        </Box>
      )}
      <CompletePracticeRegistrationModal
        open={registrationModalOpen}
        onClose={() => setRegistrationModalOpen(false)}
      />
      <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
    </>
  );
};

export default CustomToolbar;
